import { z } from 'zod';
import { assetIdSchema, assetPathSchema } from '../assets';

/**
 * What a skin's PNG must declare before it can be worn (ADR 0041).
 *
 * ## A skin is a NEW ship PNG
 *
 * Not the ordinary hull recoloured, not a tint, not a filter over the art that
 * is already there. A skin ships its own image of a whole ship, and the runtime
 * draws that image INSTEAD of the hull's. That is the decision, and this schema
 * is what makes it checkable: a definition that claims to be a skin and has no
 * file of its own cannot pass.
 *
 * ## Nothing is inferred from the file name
 *
 * The project's hard rule (`assets.ts`) applies here in full. The pivot, the
 * facing, the base scale, the cannon hardpoint and the visual bounds are all
 * explicit fields. Renaming a file changes nothing about how it is drawn, and a
 * file whose metadata is missing is refused rather than guessed at.
 *
 * ## Normalised coordinates, never viewport pixels
 *
 * Every position in this file is a fraction of the IMAGE - `0..1` across and
 * down - or a fraction of the hull's own drawn size. None of it is pixels of a
 * screen, because the same skin is drawn at three visual scales, at three
 * camera zooms and at two viewport sizes, and a pixel offset would be correct
 * in exactly one of those combinations.
 */

/** Bumped when this contract changes in a way old metadata cannot satisfy. */
export const SKIN_ASSET_FORMAT_VERSION = 1;

/**
 * The direction the art points, and there is only one.
 *
 * `HuntScene.syncShipOrientation` rotates the hull by `facing + PI/2` and says
 * why: "sprite art points up, which is `-PI/2` in world terms". `PvpScene` does
 * the same through `pvpHullRotation`. Every one of the twenty shipped hulls is
 * drawn nose-up, so a skin that pointed any other way would fly sideways in
 * both modes.
 *
 * It is a literal rather than an enum with one member so that a second
 * convention cannot be added without somebody editing this line and reading the
 * paragraph above it.
 */
export const SKIN_ART_FACING = 'up' as const;

/**
 * The size band a skin PNG must land in.
 *
 * The floor is 256 because the hull is drawn up to three times its base size
 * and a 128-pixel image at 3x on a 1920 viewport is visibly soft. The ceiling
 * is 2048, which is the largest master this pipeline already produces (the pet
 * renders) - a limit borrowed rather than invented, so a skin cannot be the one
 * asset that blows a texture budget every other category respects.
 *
 * The shipped hulls are 1024x1024 and 1254x1254, both comfortably inside.
 */
export const SKIN_PNG_MIN_DIMENSION = 256;
export const SKIN_PNG_MAX_DIMENSION = 2048;

/** A normalised point inside the image. `{0.5, 0.5}` is its centre. */
export const skinImagePointSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
});
export type SkinImagePoint = z.infer<typeof skinImagePointSchema>;

/**
 * A point in HULL-LOCAL units: forward along the nose, lateral to the right.
 *
 * The unit is the hull's drawn diameter, so `forward: 0.5` is exactly the
 * nose and `lateral: 0.25` is a quarter of a hull to starboard - a proportion
 * of the ship, never a length in world units, which is what keeps a skin
 * correct at 1x and at 3x without a second number.
 *
 * Its one consumer today is the VFX layer, which measures overlay, trail and
 * signature offsets this way. It used to have a second - `cannonHardpoint` -
 * and the removal of that field (gate V24D) left this schema decorative but
 * still exported. It stays because the reading is the vocabulary a skin's
 * offsets are written in; it is NOT a place to hang another one.
 */
export const skinHullPointSchema = z.object({
  /** Towards the nose. Positive is forward; the nose is at +0.5. */
  forward: z.number().min(-1.5).max(1.5),
  /** To the hull's right. */
  lateral: z.number().min(-1.5).max(1.5),
});
export type SkinHullPoint = z.infer<typeof skinHullPointSchema>;

/**
 * The rectangle of the image the art actually occupies, normalised.
 *
 * Declared rather than measured from the alpha channel, and both are checked:
 * the validator reads the real alpha bounding box and refuses metadata that
 * claims more art than the file contains. It exists so a skin with deliberate
 * empty margin - a long flame tail, say - can say so, instead of the runtime
 * inferring a size from transparent pixels.
 */
export const skinVisualBoundsSchema = z
  .object({
    left: z.number().min(0).max(1),
    top: z.number().min(0).max(1),
    right: z.number().min(0).max(1),
    bottom: z.number().min(0).max(1),
  })
  .refine((bounds) => bounds.right > bounds.left && bounds.bottom > bounds.top, {
    message: 'visual bounds must enclose a positive area',
  });
export type SkinVisualBounds = z.infer<typeof skinVisualBoundsSchema>;

export const skinAssetMetadataSchema = z
  .object({
    formatVersion: z.literal(SKIN_ASSET_FORMAT_VERSION),
    /** The catalogue's `assetKey`. One skin, one key, and it must be unique. */
    assetKey: assetIdSchema,
    /** Path under the asset root, e.g. `skins/violet-pulse.png`. */
    file: assetPathSchema,
    /**
     * SHA-256 of the PNG bytes, lower-case hex.
     *
     * The art and its metadata are one thing. If the file changes and this does
     * not, the validator refuses - so a skin cannot be swapped for different art
     * after it was approved, and a banner cannot be selling a picture nobody
     * signed off.
     */
    sha256: z.string().regex(/^[0-9a-f]{64}$/, 'sha256 must be lower-case hex'),
    /** Real pixel dimensions, checked against the file. */
    width: z.int().min(SKIN_PNG_MIN_DIMENSION).max(SKIN_PNG_MAX_DIMENSION),
    height: z.int().min(SKIN_PNG_MIN_DIMENSION).max(SKIN_PNG_MAX_DIMENSION),
    /** Alpha is required: a ship on an opaque rectangle is a rectangle. */
    hasAlpha: z.literal(true),
    facing: z.literal(SKIN_ART_FACING),
    /**
     * The anchor the sprite rotates about, normalised in the IMAGE.
     *
     * `{0.5, 0.5}` for every shipped hull. It is declarable because a skin may
     * legitimately hang art off one end - a long exhaust - and still need to
     * turn about the ship rather than about the middle of its own canvas.
     */
    pivot: skinImagePointSchema,
    /**
     * Where the SHIP's centre sits in the image, normalised.
     *
     * Usually the same as the pivot and deliberately a separate field: the
     * pivot is what Phaser rotates about, and this is what the game means by
     * "the ship is here" when it places a team mark, a health bar or a magic
     * circle. Letting them differ is what allows decorative overhang without
     * the HUD drifting off the hull.
     */
    hullCenter: skinImagePointSchema,
    /**
     * How much of the image is HULL, as a fraction of the image height.
     *
     * The runtime scales art so the hull covers a fixed world size, dividing by
     * the texture's own height (`pvpSpriteScale`, and `HuntScene` the same
     * way). A skin whose ship fills 70% of a tall canvas would therefore be
     * drawn 30% too small unless it can say so. This is that correction, and it
     * is 1 for art framed like the shipped hulls.
     */
    baseScale: z.number().gt(0).max(4),
    /*
     * There is deliberately no `cannonHardpoint` here (gate V24D).
     *
     * It used to sit exactly at this line, and its own documentation said what
     * it was for: "the ORIGIN that formation is measured from". A skin that
     * moves the origin the formation is measured from moves the cannons, and
     * moving the cannons from skin art is the single thing every gate since
     * V22D.1 has forbidden.
     *
     * The field never reached the compiled plan - V23.1C already kept it out -
     * so by the end nothing read it except the rule that checked it was near
     * the hull, a rule whose only purpose was to validate the field itself. A
     * strict schema is a promise about what a descriptor may say, and the
     * strongest form of "a skin cannot place a cannon" is that there is no
     * syntax for it. `.strict()` now REFUSES a descriptor that declares one.
     *
     * Cannons come from `cannonFormation(level)`, and from nowhere else.
     */
    visualBounds: skinVisualBoundsSchema,
    /** Bumped by the author whenever the art changes. Cache busting. */
    version: z.int().min(1),
    /** Free-form note for the pipeline. Never parsed by the game. */
    note: z.string().max(280).optional(),
  })
  .strict()
  .refine((asset) => asset.file.endsWith('.png'), {
    message: 'a skin asset must be a .png',
    path: ['file'],
  })
  .refine(
    (asset) =>
      asset.hullCenter.x >= asset.visualBounds.left &&
      asset.hullCenter.x <= asset.visualBounds.right &&
      asset.hullCenter.y >= asset.visualBounds.top &&
      asset.hullCenter.y <= asset.visualBounds.bottom,
    {
      message: 'the hull centre must lie inside the visual bounds',
      path: ['hullCenter'],
    },
  );

export type SkinAssetMetadata = z.infer<typeof skinAssetMetadataSchema>;

/**
 * Everything structurally wrong with one asset record, as a list.
 *
 * A list rather than the first failure, so an author fixes a file once instead
 * of once per run. The zod schema catches shape and range; this catches the
 * relationships between fields, which a schema cannot express readably.
 *
 * It deliberately says nothing about the FILE - whether it exists, decodes,
 * carries alpha or hashes to what was declared. That needs the filesystem, and
 * it lives in the validator.
 */
export const SKIN_ASSET_DEFECTS = ['pivot_outside_bounds', 'degenerate_bounds'] as const;
export type SkinAssetDefect = (typeof SKIN_ASSET_DEFECTS)[number];

export function skinAssetDefects(asset: SkinAssetMetadata): SkinAssetDefect[] {
  const defects: SkinAssetDefect[] = [];
  if (
    asset.pivot.x < asset.visualBounds.left ||
    asset.pivot.x > asset.visualBounds.right ||
    asset.pivot.y < asset.visualBounds.top ||
    asset.pivot.y > asset.visualBounds.bottom
  ) {
    defects.push('pivot_outside_bounds');
  }
  const area =
    (asset.visualBounds.right - asset.visualBounds.left) *
    (asset.visualBounds.bottom - asset.visualBounds.top);
  if (area < 0.01) {
    // A hundredth of the canvas. Below that the "art" is a speck and the
    // declared bounds are almost certainly a typo.
    defects.push('degenerate_bounds');
  }
  return defects;
}
